import { PaymentRequestModal } from '@lightspeed/invoicing-library'
import React from 'react'
import ReactDOM from 'react-dom'
import { ToastNotifications } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import isEqual from 'lodash/isEqual'
import { PaymentRequestFormData } from '../../../../../scripts/models/invoicing-payment-request'
import { saveSaleAndCreatePaymentRequest } from '../../../../functions/invoicing/save-sale-and-create-payment-request'
import {
  buildPaymentRequestDto,
  filterLineItemsForPaymentRequest,
  getPreviewURL,
} from '../../../../functions/invoicing/payment-request-resources'
import {
  useRegisterManager,
  useRetailerSettingsManager,
  useSaleManager,
  useUserManager,
} from '../../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import {
  addPaymentRequestSaleAttribute,
  generateLoyaltyClaimCode,
  selectCustomer,
  selectLineItems,
  selectSaleAttributesHasPaymentRequest,
  selectSaleFinals,
  selectSaleId,
  selectSaleSummaryFields,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { useGoConfirmation } from '../hooks/useGoConfirmation'
import { setPaymentRequestSuccess } from '../../../../state/slices/posState/posStateSlice'
import { useFetchPaymentTypes } from '../hooks/useFetchPaymentTypes'
import { useFulfillmentTakeNowSaleData } from '../../useFulfillmentTakeNowSaleData'
import { LineItem } from '../../../../models/line-item'

interface PaymentRequestModalProps {
  showPaymentRequestModal: boolean
  setShowPaymentRequestModal: () => void
  amountTendered: string // is the amount entered for partial payments on the Payment Panel, if not edited then equals the sale outstanding balance
  canLayaway?: boolean
}

export const PaymentRequestModalWrapper: React.FC<PaymentRequestModalProps> = ({
  showPaymentRequestModal,
  setShowPaymentRequestModal,
  amountTendered,
  canLayaway,
}) => {
  const t = useT()
  const registerManager = useRegisterManager()
  const currentOutlet = registerManager.getActiveOutlet()
  const currentRegister = registerManager.getActiveRegister()
  const customer = useAppSelector(selectCustomer)
  const saleLineItems = useAppSelector(selectLineItems, isEqual)
  const saleIdFromStore = useAppSelector(selectSaleId)
  const saleFinals = useAppSelector(selectSaleFinals)
  const retailer = useRetailerSettingsManager().get()
  const { discount } = useAppSelector(selectSaleSummaryFields)
  const saleManager = useSaleManager()
  const dispatch = useAppDispatch()
  const goConfirmation = useGoConfirmation()
  const userManager = useUserManager()
  const user = userManager.getLoggedInUser()
  const hasPaymentRequestAttribute = useAppSelector(
    selectSaleAttributesHasPaymentRequest
  )
  const { systemPaymentTypes } = useFetchPaymentTypes()

  let lineItems: LineItem[] = []

  const {
    shouldShowGroupingOfSaleLines,
    takeNowLines: takeNowLinesData,
  } = useFulfillmentTakeNowSaleData()

  // For fulfillment with take now items, if grouping of sale lines is enabled,
  // filter out take now lines because they will be paid for up-front when the items goes with the customer
  // For other fulfillment items, remote payment request is allowed
  if (shouldShowGroupingOfSaleLines) {
    lineItems = filterLineItemsForPaymentRequest(
      saleLineItems,
      takeNowLinesData
    )
  } else {
    lineItems = saleLineItems
  }

  const paymentRequestDto = buildPaymentRequestDto({
    customer,
    lineItems,
    saleIdFromStore,
    saleFinals,
    currentOutlet,
    currentRegister,
    retailer,
    discount,
    amountTendered,
    user,
    lspayPaymentTypeId: systemPaymentTypes.lspayPaymentType?.id,
  })

  const previewURL = getPreviewURL(retailer)

  // add paymentrequest to sale attributes to indicate a payment request was created for this sale
  const addPaymentRequestAttribute = () => {
    if (!hasPaymentRequestAttribute) {
      dispatch(addPaymentRequestSaleAttribute())
    }
  }

  // this payment request success state is used to show info on the sale confirmation screen
  const setPaymentRequestSuccessState = (
    paymentRequestId: string,
    requestedAmount: number
  ) => {
    dispatch(
      setPaymentRequestSuccess({
        paymentRequestId,
        requestedAmount,
        ...(canLayaway && { headerTitle: 'Layaway' }),
      })
    )
  }

  const showSuccessToast = (paymentRequestId: string, email: string) => {
    ToastNotifications.success(
      t(`Payment request #${paymentRequestId} sent to ${email}`, {
        _context: 'Payment request creation success toast',
      })
    )
  }

  const showFailureToast = (error?: string) => {
    const errorMessage = error
      ? `Failed to create payment request: ${error}`
      : 'Failed to create payment request'
    ToastNotifications.negative(
      t(errorMessage, {
        _context: 'Payment request creation error toast',
      })
    )
  }

  const handleSubmit = async (formData: PaymentRequestFormData) => {
    // Add attribute to sale redux state
    addPaymentRequestAttribute()

    const result = await saveSaleAndCreatePaymentRequest(
      saleManager,
      formData.paymentRequestDto
    )

    if (!result.success) {
      showFailureToast(result.error)
      return
    }

    // If canLayaway, transition sale to layby status AFTER payment request succeeds
    // This ensures we don't leave sale in layby status if payment request creation fails
    if (canLayaway) {
      // mirror logic from useLaybyOnAccountSale hook for handling layby sales
      dispatch(generateLoyaltyClaimCode())
      await saleManager.laybySale()
    }

    setPaymentRequestSuccessState(
      result.paymentRequestId!,
      result.requestedAmount || formData.paymentRequestDto.requestedAmount
    )

    showSuccessToast(
      result.paymentRequestId!,
      formData.paymentRequestDto.sendToEmail
    )

    // Navigate to the sale confirmation screen.
    goConfirmation()

    // Close modal
    setShowPaymentRequestModal()
  }

  if (!showPaymentRequestModal) return null

  return ReactDOM.createPortal(
    <PaymentRequestModal
      open={showPaymentRequestModal}
      setOpen={setShowPaymentRequestModal}
      customerEmail={''}
      setCustomerEmail={(_value: string) => {}} // No-op function as modal handles its own state
      documentURL={previewURL}
      onSubmit={handleSubmit}
      handleCancel={setShowPaymentRequestModal}
      paymentRequestDto={paymentRequestDto}
      showAmountField={false}
      showItemsToggle={false}
      {...(canLayaway && { submitButtonText: 'Send request and Layaway' })}
    />,
    document.body
  )
}
